import { MOCK_POWERS, type PowerInfo, type PowerPawnInfo, mergePowerWithMockExtras } from '@/lib/powers-data';
import type { AceAvatarFrame } from '@/lib/ace-avatar-frames';
import type { ArticleContent } from '@/lib/articleContent';
import type { CmsPublicationStatus } from '@/lib/cms-publication';
import { fixMojibake } from '@/lib/fix-mojibake';
import {
  normalizeRelatedExplore,
  relatedExploreForPersist,
  type RelatedExploreConfig,
} from '@/lib/related-explore';
import { isCalloutColorId, type CalloutColorId } from '@/lib/callout-colors';
import {
  normalizePowerDocSections,
  powerDocSectionsForPersist,
  type PowerDocSection,
} from '@/lib/power-doc-section';
import {
  normalizePowerLore,
  powerLoreForPersist,
  migratePowerLoreFields,
  type PowerLore,
} from '@/lib/power-lore';
import {
  powerToPstyleEditorInput,
  pstyleEditorInputToCmsConfig,
  type PowerPstyleEditorInput,
} from '@/lib/power-pstyle-cms';

export const POWERS_CMS_STORAGE_KEY = 'xat-help-powers-cms-v1';

export type PowerCmsRecord = PowerInfo & {
  createdAt: string;
  updatedAt: string;
};

export type PowersCmsState = {
  powers: PowerCmsRecord[];
};

function nowIso() {
  return new Date().toISOString();
}

function toRecord(power: PowerInfo, timestamps?: { createdAt: string; updatedAt: string }): PowerCmsRecord {
  const t = timestamps ?? { createdAt: nowIso(), updatedAt: nowIso() };
  return { ...power, ...t };
}

export function seedPowersFromMock(): PowerCmsRecord[] {
  const t = nowIso();
  return MOCK_POWERS.map((p) => toRecord(p, { createdAt: t, updatedAt: t }));
}

/** Powers com conteúdo curado no repositório — sincronizados do mock ao carregar o CMS. */
const CURATED_MOCK_SYNC_SLUGS = new Set(['everypower']);

function syncPowerRecordFromMock(existing: PowerCmsRecord, mock: PowerInfo, updatedAt: string): PowerCmsRecord {
  return {
    ...existing,
    name: mock.name,
    category: mock.category,
    type: mock.type,
    limited: mock.limited,
    released: mock.released,
    prices: { ...mock.prices },
    popularity: mock.popularity ?? existing.popularity,
    description: { ...mock.description },
    smilies: mock.smilies.length > 0 ? [...mock.smilies] : existing.smilies,
    pawns: mock.pawns.length > 0 ? mock.pawns.map((p) => ({ ...p })) : existing.pawns,
    updatedAt,
  };
}

/** Inclui powers novos do mock que ainda não existem no CMS (ex.: after deploy). */
export function mergePowersCmsWithMockSeed(stored: PowerCmsRecord[]): PowerCmsRecord[] {
  const bySlug = new Map(stored.map((p) => [p.slug.toLowerCase(), p]));
  const t = nowIso();
  let changed = false;

  for (const mock of MOCK_POWERS) {
    const key = mock.slug.toLowerCase();
    const existing = bySlug.get(key);

    if (!existing) {
      bySlug.set(key, toRecord(mock, { createdAt: t, updatedAt: t }));
      changed = true;
      continue;
    }

    if (CURATED_MOCK_SYNC_SLUGS.has(key)) {
      bySlug.set(key, syncPowerRecordFromMock(existing, mock, t));
      changed = true;
      continue;
    }

    const shouldRefreshSmilies =
      mock.smilies.length > (existing.smilies?.length ?? 0);
    const shouldRefreshPawns =
      mock.pawns.length > (existing.pawns?.length ?? 0);

    if (shouldRefreshSmilies || shouldRefreshPawns) {
      bySlug.set(key, {
        ...existing,
        smilies:
          shouldRefreshSmilies && mock.smilies.length > 0
            ? mock.smilies
            : existing.smilies,
        pawns:
          shouldRefreshPawns && mock.pawns.length > 0 ? mock.pawns : existing.pawns,
        updatedAt: t,
      });
      changed = true;
    }
  }

  if (!changed) return stored;
  return Array.from(bySlug.values());
}

export function loadPowersCmsState(): PowersCmsState {
  return { powers: [] };
}

export function savePowersCmsState(state: PowersCmsState) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(POWERS_CMS_STORAGE_KEY, JSON.stringify(state));
}

export function slugifyPowerName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '')
    .replace(/^-+|-+$/g, '');
}

export function getCategoryCountFor(powers: PowerInfo[], category: string): number {
  if (category === 'limited') return powers.filter((p) => p.limited).length;
  if (category === 'unlimited') return powers.filter((p) => !p.limited).length;
  return powers.filter((p) => p.category === category).length;
}

export function emptyPowerTutorial(): ArticleContent {
  return { intro: '', blocks: [] };
}

export type PowerEditorInput = {
  name: string;
  slug: string;
  id: number;
  category: string;
  limited: boolean;
  released: string;
  storePrice: string;
  fairtradeMin: number;
  fairtradeMax: number;
  descriptionShort: string;
  descriptionLong: string;
  /** Cabeçalho «Sobre este power» na página pública. */
  overviewSmiley: string;
  overviewSubtitle: string;
  overviewTitle: string;
  smilies: string[];
  stickers: string[];
  pawns: PowerPawnInfo[];
  tutorialEnabled: boolean;
  tutorial: ArticleContent;
  pstyle: PowerPstyleEditorInput;
  avatarFramesEnabled: boolean;
  avatarFrames: AceAvatarFrame[];
  /** Secções de documentação custom — desligadas por padrão. */
  docSectionsEnabled: boolean;
  docSections: PowerDocSection[];
  /** Lore / história editorial — desligada por padrão. */
  lore: PowerLore;
  /** Caixas de informação da página pública (vazio = texto automático). */
  calloutHowItWorks: string;
  calloutHowItWorksTitle: string;
  calloutHowItWorksColor: CalloutColorId;
  calloutLimited: string;
  calloutLimitedTitle: string;
  calloutLimitedColor: CalloutColorId;
  relatedExplore: RelatedExploreConfig;
  publicationStatus?: CmsPublicationStatus;
};

export function powerToEditorInput(power: PowerInfo): PowerEditorInput {
  const hasTutorial = Boolean(
    power.tutorial && (power.tutorial.intro.trim() || power.tutorial.blocks.length > 0)
  );
  // Mesma enriquecimento da página pública (ex.: Lore do Topman no seed).
  const migrated = migratePowerLoreFields(mergePowerWithMockExtras(power));
  return {
    name: migrated.name,
    slug: migrated.slug,
    id: migrated.id,
    category: migrated.category,
    limited: migrated.limited,
    released: migrated.released?.slice(0, 10) || '',
    storePrice: String(migrated.prices.store ?? ''),
    fairtradeMin: migrated.prices.fairtrade_min,
    fairtradeMax: migrated.prices.fairtrade_max,
    descriptionShort: fixMojibake(migrated.description.short ?? ''),
    descriptionLong: fixMojibake(migrated.description.long ?? ''),
    overviewSmiley: (migrated.overviewSmiley ?? 'read').replace(/[()]/g, '').trim() || 'read',
    overviewSubtitle: fixMojibake(migrated.overviewSubtitle ?? 'Documentação'),
    overviewTitle: fixMojibake(migrated.overviewTitle ?? 'Sobre este power'),
    smilies: [...migrated.smilies],
    stickers: [...(migrated.stickers ?? [])],
    pawns: migrated.pawns?.map((p) => ({ ...p })) ?? [],
    tutorialEnabled: hasTutorial,
    tutorial: migrated.tutorial
      ? { ...migrated.tutorial, blocks: [...migrated.tutorial.blocks] }
      : emptyPowerTutorial(),
    pstyle: powerToPstyleEditorInput(migrated),
    avatarFramesEnabled: migrated.avatarFramesEnabled ?? false,
    avatarFrames: (migrated.avatarFrames ?? []).map((f) => ({
      ...f,
      cdnAlternates: f.cdnAlternates ? [...f.cdnAlternates] : undefined,
    })),
    docSectionsEnabled: migrated.docSectionsEnabled ?? false,
    docSections: normalizePowerDocSections(migrated.docSections),
    lore: normalizePowerLore(migrated.lore),
    calloutHowItWorks: migrated.calloutHowItWorks ?? '',
    calloutHowItWorksTitle: migrated.calloutHowItWorksTitle ?? '',
    calloutHowItWorksColor: isCalloutColorId(migrated.calloutHowItWorksColor)
      ? migrated.calloutHowItWorksColor
      : 'info',
    calloutLimited: migrated.calloutLimited ?? '',
    calloutLimitedTitle: migrated.calloutLimitedTitle ?? '',
    calloutLimitedColor: isCalloutColorId(migrated.calloutLimitedColor)
      ? migrated.calloutLimitedColor
      : 'warning',
    relatedExplore: normalizeRelatedExplore(migrated.relatedExplore),
    publicationStatus: migrated.publicationStatus,
  };
}

/** Campos preenchíveis a partir da API xatBlog (sem tutorial nem callouts do painel). */
export function apiPowerToEditorPatch(
  power: PowerInfo
): Omit<
  PowerEditorInput,
  | 'tutorialEnabled'
  | 'tutorial'
  | 'docSectionsEnabled'
  | 'docSections'
  | 'lore'
  | 'overviewSmiley'
  | 'overviewSubtitle'
  | 'overviewTitle'
  | 'calloutHowItWorks'
  | 'calloutHowItWorksTitle'
  | 'calloutHowItWorksColor'
  | 'calloutLimited'
  | 'calloutLimitedTitle'
  | 'calloutLimitedColor'
  | 'relatedExplore'
> {
  const base = powerToEditorInput(power);
  const {
    tutorialEnabled: _t,
    tutorial: _u,
    docSectionsEnabled: _ds,
    docSections: _dsec,
    lore: _lore,
    overviewSmiley: _os,
    overviewSubtitle: _osu,
    overviewTitle: _ot,
    calloutHowItWorks: _h,
    calloutHowItWorksTitle: _ht,
    calloutHowItWorksColor: _hc,
    calloutLimited: _l,
    calloutLimitedTitle: _lt,
    calloutLimitedColor: _lc,
    relatedExplore: _r,
    ...rest
  } = base;
  return rest;
}

/** Pré-visualização mínima para resolver preços fairtrade como na página pública. */
export function editorInputToPowerPreview(input: PowerEditorInput): PowerInfo | null {
  const slug = input.slug.trim().toLowerCase();
  if (!slug) return null;

  return {
    id: input.id,
    name: input.name.trim() || slug,
    slug,
    description: {
      short: input.descriptionShort,
      long: input.descriptionLong,
    },
    prices: {
      store: String(input.storePrice),
      fairtrade_min: input.fairtradeMin,
      fairtrade_max: input.fairtradeMax,
    },
    status: input.limited ? 'limited' : false,
    released: input.released,
    category: input.category,
    type: input.category,
    limited: input.limited,
    smilies: input.smilies,
    stickers: input.stickers,
    pawns: input.pawns,
  };
}

export function editorInputToPower(
  input: PowerEditorInput,
  existing?: PowerCmsRecord
): Omit<PowerCmsRecord, 'createdAt' | 'updatedAt'> {
  return {
    id: input.id,
    name: input.name.trim(),
    slug: input.slug.trim().toLowerCase(),
    category: input.category,
    limited: input.limited,
    released: input.released,
    status: input.limited ? 'limited' : false,
    description: {
      short: fixMojibake(input.descriptionShort.trim()),
      long: fixMojibake(input.descriptionLong.trim()),
    },
    prices: {
      store: input.storePrice.trim(),
      fairtrade_min: input.fairtradeMin,
      fairtrade_max: input.fairtradeMax,
    },
    smilies: input.smilies.map((s) => s.trim()).filter(Boolean),
    stickers: input.stickers.map((s) => s.trim()).filter(Boolean),
    pawns: input.pawns
      .map((p) => ({ hat: p.hat.trim(), name: p.name.trim() }))
      .filter((p) => p.hat || p.name),
    categories: existing?.categories,
    group: existing?.group,
    type: existing?.type ?? input.category,
    popularity: existing?.popularity,
    collection: existing?.collection,
    image: existing?.image,
    tutorial:
      input.tutorialEnabled &&
      (input.tutorial.intro.trim() || input.tutorial.blocks.length > 0)
        ? input.tutorial
        : undefined,
    pstyle: pstyleEditorInputToCmsConfig(input.pstyle),
    avatarFramesEnabled: input.avatarFramesEnabled,
    avatarFrames: input.avatarFramesEnabled
      ? input.avatarFrames
          .filter((f) => f.id.trim() && f.name.trim())
          .map((f) => ({
            ...f,
            id: f.id.trim().toLowerCase(),
            name: f.name.trim(),
            imageUrl: f.imageUrl?.trim() || undefined,
            shape: f.shape ?? 'square',
          }))
      : [],
    docSectionsEnabled: input.docSectionsEnabled,
    docSections: input.docSectionsEnabled
      ? powerDocSectionsForPersist(input.docSections).map((s) => ({
          ...s,
          description: fixMojibake(s.description),
          title: fixMojibake(s.title),
          subtitle: fixMojibake(s.subtitle),
        }))
      : [],
    lore: (() => {
      const persisted = powerLoreForPersist({
        ...input.lore,
        subtitle: fixMojibake(input.lore.subtitle),
        title: fixMojibake(input.lore.title),
        intro: fixMojibake(input.lore.intro),
        chapters: input.lore.chapters.map((c) => ({
          ...c,
          title: fixMojibake(c.title),
          body: fixMojibake(c.body),
        })),
        curiosities: input.lore.curiosities.map((c) => ({
          ...c,
          title: fixMojibake(c.title),
          body: fixMojibake(c.body),
        })),
      });
      // Sempre enviar objecto para o merge do backend sobrescrever lore antiga.
      return (
        persisted ?? {
          enabled: false,
          subtitle: '',
          title: '',
          smiley: '',
          intro: '',
          theme: 'normal',
          chapters: [],
          curiosities: [],
        }
      );
    })(),
    overviewSmiley: input.overviewSmiley.replace(/[()]/g, '').trim() || 'read',
    overviewSubtitle: fixMojibake(input.overviewSubtitle.trim()) || 'Documentação',
    overviewTitle: fixMojibake(input.overviewTitle.trim()) || 'Sobre este power',
    // Enviar '' (não undefined) para conseguir limpar o texto guardado no backend.
    calloutHowItWorks: fixMojibake(input.calloutHowItWorks.trim()),
    calloutHowItWorksTitle: input.calloutHowItWorksTitle.trim(),
    calloutHowItWorksColor: input.calloutHowItWorksColor,
    calloutLimited: fixMojibake(input.calloutLimited.trim()),
    calloutLimitedTitle: input.calloutLimitedTitle.trim(),
    calloutLimitedColor: input.calloutLimitedColor,
    relatedExplore: relatedExploreForPersist(input.relatedExplore),
    publicationStatus: input.publicationStatus ?? existing?.publicationStatus,
  };
}
